/**
 * Типы для планов подписки (абонементов)
 */

import type { Language } from '@masters-club/types';

type LanguageCode = Language['code'];

/** Данные перевода плана подписки */
interface SubscriptionPlanTranslation {
  name?: string;
  description?: string;
  shortDescription?: string;
}

type TranslationsRecord = Record<string, SubscriptionPlanTranslation>;

/**
 * Типы целей доступа
 * Для абонементов используются только COURSE и CHANNEL
 */
export type AccessTargetType = 'COURSE' | 'CHANNEL';

/**
 * Право доступа в плане подписки
 */
export interface SubscriptionPlanEntitlement {
  id: number;
  subscriptionPlanId: number;
  targetType: AccessTargetType;
  targetId: number | null;
  accessDurationDays: number | null;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPlanPricingTier {
  id: number;
  prices: Record<string, number>;
  baseCurrency: string;
  isActive: boolean;
  order: number;
}

/**
 * План подписки
 */
export interface SubscriptionPlan {
  id: number;
  code: string;
  price: string;
  currency: string;
  prices: Record<string, number> | null;
  baseCurrency: string;
  saleStartDate: string | null;
  saleEndDate: string | null;
  durationDays: number | null;
  validUntil: string | null;
  isFamily: boolean;
  familySlots: number;
  isActive: boolean;
  order: number;
  imageId: number | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  translations?: TranslationsRecord;
  entitlements?: SubscriptionPlanEntitlement[];
  pricingTiers?: SubscriptionPlanPricingTier[];
}

/**
 * Данные формы создания/редактирования плана
 * Большинство полей (цена, срок, права доступа, переводы, изображение)
 * теперь управляются через тарифы (pricing tiers).
 */
export interface SubscriptionPlanFormData {
  code: string;
}

/**
 * Получить название плана подписки из переводов
 */
export function getSubscriptionPlanName(plan: SubscriptionPlan, languageCode: LanguageCode = 'ru'): string {
  const translation = plan.translations?.[languageCode];
  if (translation && typeof translation === 'object' && 'name' in translation) {
    return (translation as { name?: string }).name ?? plan.code;
  }
  return plan.code;
}

/**
 * Алиас для getSubscriptionPlanName
 * @deprecated Используйте getSubscriptionPlanName
 */
export const getPlanName = getSubscriptionPlanName;

/**
 * Форматирование цены
 */
export function formatPrice(price: string, currency: string): string {
  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {return `${price} ${currency}`;}
  return new Intl.NumberFormat('ru-RU').format(numPrice) + ` ${currency}`;
}

/**
 * Форматирование срока действия
 */
export function formatDuration(days: number | null): string {
  if (days === null) {return 'Бессрочно';}
  if (days === 1) {return '1 день';}
  if (days <= 4) {return `${days} дня`;}
  if (days === 7) {return '1 неделя';}
  if (days === 14) {return '2 недели';}
  if (days === 30) {return '1 месяц';}
  if (days === 90) {return '3 месяца';}
  if (days === 180) {return '6 месяцев';}
  if (days === 365) {return '1 год';}
  return `${days} дней`;
}

/**
 * Опции типов целей доступа для абонементов
 */
export const TARGET_TYPE_OPTIONS = [
  { value: 'COURSE', label: 'Курс' },
  { value: 'CHANNEL', label: 'Канал/Чат' },
] as const;

/**
 * Получить label для типа цели
 */
export function getTargetTypeLabel(type: AccessTargetType): string {
  return TARGET_TYPE_OPTIONS.find(opt => opt.value === type)?.label ?? type;
}
